import { ProductStats } from '../routes/products/store/Product';
import { PulseKeyDto } from '../routes/pulse-keys/store/PulseKey';
import { KeySearchFilters } from '../routes/pulse-keys/store/PulseKeySearch';
import {
  PulseKeyStatDto,
  PulseKeyStatParams,
} from '../routes/statistics/store/PulseKeyStatRequest';
import { QueryParams } from '../store/utils/query/Query';
import { QueryResponseDto } from '../store/utils/query/QueryResponse';
import ApiHttpClient from './ApiHttpClient';

export default class PulseKeys {
  constructor(private readonly client: ApiHttpClient) {}

  async changeKeyCustomer(keyId: string, newCustomerEmail: string) {
    return this.client.post(`v1/provider/pulseKeys/${keyId}/changeCustomer`, {
      email: newCustomerEmail,
    });
  }

  async disableKey(keyId: string) {
    return this.client.post(`v1/provider/pulseKeys/${keyId}/disable`);
  }

  async enableKey(keyId: string) {
    return this.client.post(`v1/provider/pulseKeys/${keyId}/enable`);
  }

  async findKeys(query: QueryParams): Promise<QueryResponseDto<PulseKeyDto>> {
    return this.client.get(`v1/provider/pulseKeys`, {
      query: {
        // todo: rename productId to sellableId on API side
        productId: query.sellableId,
        ...query,
      },
    });
  }

  async generateProductKeys(
    productId: string,
    numOfKeys: number,
    label?: string,
  ): Promise<PulseKeyDto[]> {
    return this.client.post(
      `v1/provider/pulseKeys/generate?productId=${productId}`,
      { numOfKeys, label },
    );
  }

  getExportToCsvUrl(query: QueryParams<KeySearchFilters>): string {
    const apiBaseUrl = this.client.baseUrl;

    const url = new URL(`v1/provider/pulseKeys/export.csv`, apiBaseUrl);

    const params = {
      ...query,
      // todo: rename productId to sellableId on API side
      productId: query.sellableId,
      redeemedAtFrom: toIso(query.redeemedAtFrom),
      redeemedAtTo: toIso(query.redeemedAtTo),
    };

    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        url.searchParams.append(key, String(value));
      }
    });

    return url.href;
  }

  async getProductKeys(productId: string): Promise<PulseKeyDto[]> {
    return this.client.get(`v1/provider/pulseKeys?productId=${productId}`);
  }

  async getProductStats(productId: string): Promise<ProductStats> {
    return this.client.get(
      `v1/provider/pulseKeys/productStats?productId=${productId}`,
    );
  }

  async getStats(filter: PulseKeyStatParams): Promise<PulseKeyStatDto[]> {
    return this.client.post('v1/provider/pulseKeys/stats', {
      ...filter,
      redeemedAtFrom: filter.redeemedAtFrom?.toISOString(),
      redeemedAtTo: filter.redeemedAtTo?.toISOString(),
    });
  }

  async importProductKeys(
    pulseKeys: PulseKeyDto[],
    productId: string,
    markDistributed = false,
  ): Promise<PulseKeyDto[]> {
    return this.client.post(`v1/provider/pulseKeys/import`, pulseKeys, {
      query: {
        productId,
        markDistributed: markDistributed ? 'true' : 'false',
      },
    });
  }

  async markAllProductKeysDistributed(productId: string) {
    return this.client.post(
      `v1/provider/pulseKeys/markDistributedForProduct?productId=${productId}`,
    );
  }

  async markSpecifiedKeysDistributed(keyIds: string[]) {
    return this.client.post(`v1/provider/pulseKeys/markDistributed`, {
      keys: keyIds,
    });
  }

  async moveCustomerKeys({
    oldCustomerEmail,
    newCustomerEmail,
  }: {
    newCustomerEmail: string;
    oldCustomerEmail: string;
  }) {
    return this.client.post(`v1/provider/pulseKeys/moveCustomerKeys`, {
      oldCustomerEmail,
      newCustomerEmail,
    });
  }

  async moveBetweenProducts({
    keyIds,
    newProductId,
  }: {
    keyIds: string[];
    newProductId: string;
  }) {
    return this.client.post(`v1/provider/pulseKeys/moveBetweenProducts`, {
      keys: keyIds,
      newProductId,
    });
  }

  async resetDownloadCount(keyId: string) {
    return this.client.post(
      `v1/provider/pulseKeys/${keyId}/resetDownloadCount`,
    );
  }

  async sendKeyToCustomer(key: string) {
    return this.client.post(`v1/provider/pulseKeys/${key}/sendKeyToCustomer`);
  }

  async setKeysLabel(keyIds: string[], label: string) {
    return this.client.post(`v1/provider/pulseKeys/setLabel`, {
      keys: keyIds,
      label,
    });
  }

  async unassignKey(keyId: string) {
    return this.client.post(`v1/provider/pulseKeys/${keyId}/unassign`);
  }
}

function toIso(date: unknown) {
  return date instanceof Date ? date.toISOString() : null;
}
